import React, { useState } from 'react';
import { FiUser, FiCalendar, FiFileText } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import './AddModals.css';


const AddTransactionModal = ({ show, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    customer: '',
    amount: '',
    status: 'pending',
    date: new Date().toISOString().split('T')[0],
    type: 'credit',
    description: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer.trim()) {
      newErrors.customer = 'Customer name is required';
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valid amount is required';
    } else {
      const amount = parseFloat(formData.amount);
      if (amount > 999999999999.99) {
        newErrors.amount = 'Amount is too large. Maximum allowed is 999,999,999,999.99';
      }
    }
    
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const dataToSend = {
        customer: formData.customer.trim(),
        amount: parseFloat(formData.amount),
        status: formData.status,
        date: formData.date,
        type: formData.type,
        description: formData.description.trim()
      };
      
      await onSave(dataToSend);
      handleClose();
    } catch (error) {
      console.error('Error saving transaction:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      customer: '',
      amount: '',
      status: 'pending',
      date: new Date().toISOString().split('T')[0],
      type: 'credit',
      description: ''
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!show) return null;

  return (
    <>
      <div className="custom-modal-backdrop" onClick={handleClose}></div>
      <div className="custom-modal add-transaction-modal">
        <button className="close-btn" onClick={handleClose}>&times;</button>
        <h2>Add New Transaction</h2>
        <form onSubmit={handleSubmit} className="add-modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">
                <FiUser className="label-icon" />
                Customer Name
              </label>
              <input
                type="text"
                value={formData.customer}
                onChange={(e) => handleInputChange('customer', e.target.value)}
                className={`form-input ${errors.customer ? 'error' : ''}`}
                placeholder="Enter customer name"
                disabled={isSubmitting}
              />
              {errors.customer && (
                <span className="error-message">{errors.customer}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <FaRupeeSign className="label-icon" />
                Amount
              </label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">₹</span>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Prevent extremely large numbers
                    if (value && parseFloat(value) > 999999999999.99) {
                      return; // Don't update if too large
                    }
                    handleInputChange('amount', value);
                  }}
                  className={`form-input amount-input ${errors.amount ? 'error' : ''}`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  max="999999999999.99"
                  disabled={isSubmitting}
                />
              </div>
              {errors.amount && (
                <span className="error-message">{errors.amount}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Transaction Type</label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="form-input"
                disabled={isSubmitting}
              >
                <option value="credit">Credit (+)</option>
                <option value="debit">Debit (-)</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="form-input"
                disabled={isSubmitting}
              >
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <FiCalendar className="label-icon" />
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                className={`form-input ${errors.date ? 'error' : ''}`}
                disabled={isSubmitting}
              />
              {errors.date && (
                <span className="error-message">{errors.date}</span>
              )}
            </div>
          </div>

          <div className="form-group description-group">
            <label className="form-label">
              <FiFileText className="label-icon" />
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="form-input form-textarea"
              placeholder="Enter transaction description..."
              rows="3"
              disabled={isSubmitting}
            />
          </div>

          <div className="modal-actions">
            <button 
              type="button" 
              className="btn btn-cancel" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isSubmitting || !formData.customer || !formData.amount}
            >
              {isSubmitting ? 'Creating...' : 'Create Transaction'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddTransactionModal;
